import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { VerifiedFood } from "@/data/verifiedFoods";
import { cn } from "@/lib/utils";
import type { Species } from "@/utils/nutritionMath";

interface FoodPresetPickerProps {
  species: Species;
  value: VerifiedFood | null;
  foods: readonly VerifiedFood[];
  onSelect: (foodId: string | null) => void;
}

const formatLabels: Record<VerifiedFood["format"], string> = {
  dry: "Dry",
  wet: "Wet",
};

const getSearchText = (food: VerifiedFood) =>
  [food.brand, food.product, food.secondaryLabel, food.kcalPerCup ? `${food.kcalPerCup} kcal cup` : ""].join(" ").toLowerCase();

export function FoodPresetPicker({ species, value, foods, onSelect }: FoodPresetPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const filteredFoods = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return foods.filter((food) => {
      if (food.species !== species) return false;
      if (!normalizedQuery) return true;
      return getSearchText(food).includes(normalizedQuery);
    });
  }, [foods, query, species]);

  const dryFoods = filteredFoods.filter((food) => food.format === "dry");
  const wetFoods = filteredFoods.filter((food) => food.format === "wet");
  const groups: Array<{ label: string; foods: VerifiedFood[] }> = [
    { label: formatLabels.dry, foods: dryFoods },
    { label: formatLabels.wet, foods: wetFoods },
  ];

  const handleSelect = (foodId: string | null) => {
    onSelect(foodId);
    setOpen(false);
    setQuery("");
  };

  const primaryLabel = value ? `${value.brand} - ${value.product}` : `Choose a popular ${species} food`;

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((current) => !current)}
        className="h-auto w-full justify-between rounded-lg px-4 py-3 text-left"
      >
        <span className="min-w-0">
          <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Popular presets</span>
          <span className="mt-1 block truncate text-sm font-semibold text-slate-900">{primaryLabel}</span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn("size-4 shrink-0 text-slate-500 transition-transform", open && "rotate-180")}
        />
      </Button>

      {value ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="rounded-full bg-blue-50 px-2.5 py-1 font-semibold text-blue-700">Verified source, May 2026</span>
          <span>{value.kcalPerKg} kcal/kg</span>
          {value.kcalPerCup ? <span>{value.kcalPerCup} kcal/cup</span> : null}
          {!value.kcalPerCup && value.secondaryLabel ? <span>{value.secondaryLabel}</span> : null}
        </div>
      ) : null}

      <p className="mt-2 text-xs leading-5 text-slate-500">
        Popular presets autofill label calories. You can still edit the numbers if your package differs.
      </p>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 rounded-xl border bg-white p-3 shadow-xl">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-slate-900">Search popular {species} foods</div>
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="text-xs font-semibold text-blue-700 transition hover:text-blue-800"
            >
              {value ? "Clear selected food" : "Use manual label entry"}
            </button>
          </div>

          <div className="relative">
            <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${species} food brand or recipe`}
              className="pl-9"
            />
          </div>

          <div className="mt-3 max-h-[min(22rem,calc(100vh-14rem))] space-y-4 overflow-y-auto pr-1">
            {groups.map((group) =>
              group.foods.length ? (
                <div key={group.label}>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{group.label}</div>
                  <div className="space-y-2">
                    {group.foods.map((food) => {
                      const isSelected = value?.id === food.id;

                      return (
                        <button
                          key={food.id}
                          type="button"
                          onClick={() => handleSelect(food.id)}
                          className={cn(
                            "w-full rounded-lg border px-3 py-3 text-left transition",
                            isSelected ? "border-blue-200 bg-blue-50" : "hover:border-slate-300 hover:bg-slate-50",
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{food.brand}</div>
                              <div className="mt-1 text-sm font-semibold text-slate-900">{food.product}</div>
                            </div>
                            {isSelected ? <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-blue-700" /> : null}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                            <span>{food.kcalPerKg} kcal/kg</span>
                            {food.kcalPerCup ? <span>{food.kcalPerCup} kcal/cup</span> : null}
                            {!food.kcalPerCup && food.secondaryLabel ? <span>{food.secondaryLabel}</span> : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null,
            )}

            {!filteredFoods.length ? (
              <div className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-slate-500">
                No foods matched that search. You can keep using manual label entry.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
